import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function submit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const user = await register(form);
            toast.success(`Welcome to Peppa Target, ${user.name.split(" ")[0]}!`);
            nav("/profile");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "Could not register");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="min-h-[80vh] grid md:grid-cols-2" data-testid="register-page">
            <div className="hidden md:block bg-ink text-white p-12 flex flex-col justify-between relative">
                <img src="https://images.unsplash.com/photo-1680992044138-ce4864c2b962?auto=format&fit=crop&w=1200&q=80" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                <div className="relative z-10">
                    <div className="flex items-center gap-2.5">
                        <img src="/logo.png" alt="Peppa Target" className="h-12 w-auto bg-white rounded p-0.5" />
                        <span className="font-display font-bold text-lg">Peppa Target</span>
                    </div>
                </div>
                <div className="relative z-10">
                    <h1 className="font-display text-4xl font-bold leading-tight">Create your account.</h1>
                    <p className="mt-3 text-white/70 max-w-sm">Join thousands of customers across East Africa shopping with Peppa Target.</p>
                </div>
            </div>
            <div className="flex items-center justify-center px-4 py-16">
                <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
                    <h2 className="font-display text-3xl font-bold mb-2">Get started</h2>
                    <p className="text-sm text-muted-foreground mb-8">Have an account? <Link to="/login" className="text-primary font-semibold">Sign in</Link></p>
                    {error && <div className="mb-4 bg-primary/10 text-primary text-sm p-3 border-l-4 border-primary">{error}</div>}
                    <label className="block mb-3">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Full name</span>
                        <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 w-full border-b border-border py-2 focus:outline-none focus:border-primary" data-testid="register-name" />
                    </label>
                    <label className="block mb-3">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Email</span>
                        <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 w-full border-b border-border py-2 focus:outline-none focus:border-primary" data-testid="register-email" />
                    </label>
                    <label className="block mb-3">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Phone (optional)</span>
                        <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 w-full border-b border-border py-2 focus:outline-none focus:border-primary" data-testid="register-phone" />
                    </label>
                    <label className="block mb-6">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Password</span>
                        <input type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1 w-full border-b border-border py-2 focus:outline-none focus:border-primary" data-testid="register-password" />
                    </label>
                    <button type="submit" disabled={loading} className="w-full bg-primary text-white py-3 font-semibold hover:bg-primary-hover disabled:opacity-50" data-testid="register-submit">
                        {loading ? "Creating account…" : "Create account"}
                    </button>
                </form>
            </div>
        </div>
    );
}
